import { AuthInterface, isAuth } from '../../Auth/AuthInterface'
import {
  SenderOrRecipientType,
  MessageType,
  ActionTaken,
  isActionTaken
} from '../../Message/MessageInterface'
import { isCarouselPayload, CarouselPayload } from './Carousel'
import { isMediaPayload, MediaPayload } from './Media'
import { isMultiSelectPayload, MultiSelectPayload } from './MultiSelect'
import { CommandPayload, isCommandPayload } from './Command'
import { isLocationPickerPayload, LocationPickerPayload } from './LocationPicker'
import { isSpacePickerPayload, SpacePickerPayload } from './SpacePicker'
import { ProfileCollectPayload, isProfileCollectPayload } from './ProfileCollect'
import { VoiceNodeCommand } from '../../../swivl-lib'

/**
 * @tsoaModel
 */
export interface AuthPayloadInterface extends AuthInterface {}

export function isAuthPayload(payload: any): payload is AuthPayloadInterface {
  return isAuth(payload)
}
/**
Actions are possibile actions. ActionTaken are the response to an action.
 */
export enum ActionType {
  URL = 'url',
  URLinNewTab = 'url_in_new_tab',
  Plain = 'plain'
}
export function isActionType(type: any): type is ActionType {
  return Object.values(ActionType).includes(type)
}

export interface ActionInterface {
  id: string
  type: ActionType
  text: string
  url?: string
}
export function isActionInterface(obj: any): obj is ActionInterface {
  if (!(obj !== undefined)) {
    return false
  }
  if ((obj as ActionInterface).type === undefined) {
    return false
  }
  if (
    ((obj as ActionInterface).type === ActionType.URL ||
      (obj as ActionInterface).type === ActionType.URLinNewTab) &&
    !((obj as ActionInterface).url !== undefined)
  ) {
    return false
  }

  return (
    obj !== undefined &&
    (obj as ActionInterface).id !== undefined &&
    (obj as ActionInterface).type !== undefined &&
    (obj as ActionInterface).text !== undefined &&
    isActionType(obj.type)
  )
}

/**
 * @tsoaModel
 */
export interface ActionTakenPayload {
  messageId: string
  actionTaken: ActionTaken
}
export function isActionTakenPayload(payload: any): payload is ActionTakenPayload {
  return (
    payload !== undefined &&
    (payload as ActionTakenPayload).messageId !== undefined &&
    (payload as ActionTakenPayload).actionTaken !== undefined &&
    isActionTaken((payload as ActionTakenPayload).actionTaken)
  )
}

/**
 * @tsoaModel
 */
export interface ButtonsPayloadInterface {
  actions: Array<ActionInterface>
}

export function isButtonsPayload(payload: any): payload is ButtonsPayloadInterface {
  return payload !== undefined && (payload as ButtonsPayloadInterface).actions !== undefined
}

/**
 * @tsoaModel
 */
export interface CreateUserAndStartConversationPayloadInterface {
  botId: string
  thirdPartyId?: string
  startOnQuest?: string
  preloadVariables?: { [key: string]: string }
}

export function isCreateUserAndStartConversationPayload(
  payload: any
): payload is CreateUserAndStartConversationPayloadInterface {
  return (
    payload !== undefined &&
    (payload as CreateUserAndStartConversationPayloadInterface).botId !== undefined
  )
}

/**
 * @tsoaModel
 */
export interface StartConversationPayloadInterface
  extends CreateUserAndStartConversationPayloadInterface {
  isNewUser?: boolean
}
export function isStartConversationPayload(
  payload: any
): payload is StartConversationPayloadInterface {
  return payload !== undefined && (payload as StartConversationPayloadInterface).botId !== undefined
}

export enum TakeoverEventType {
  TAKEOVER_STARTED = 'Started',
  TAKEOVER_ENDED = 'Ended'
}

/**
 * @tsoaModel
 */
export interface TakeoverEventPayload {
  type: TakeoverEventType
  name: string
  avatarURL: string
}
export function isTakeoverEventPayload(payload: any): payload is TakeoverEventPayload {
  return (
    payload !== undefined &&
    (payload as TakeoverEventPayload).type !== undefined &&
    ((payload as TakeoverEventPayload).type === TakeoverEventType.TAKEOVER_STARTED ||
      (payload as TakeoverEventPayload).type === TakeoverEventType.TAKEOVER_ENDED) &&
    (payload as TakeoverEventPayload).name !== undefined &&
    (payload as TakeoverEventPayload).avatarURL !== undefined
  )
}

/**
 * @tsoaModel
 */
export interface TextPayloadInterface {
  text: string
  openLinksInNewTab?: boolean
}
export function isTextPayload(payload: any): payload is TextPayloadInterface {
  return payload !== undefined && (payload as TextPayloadInterface).text !== undefined
}

export enum TypingPayloadActionType {
  Started = 'Started',
  Ended = 'Ended'
}

/**
 * @tsoaModel
 */
export interface TypingPayloadInterface {
  action: TypingPayloadActionType
}
export function isTypingPayload(payload: any): payload is TypingPayloadInterface {
  return (
    payload !== undefined &&
    (payload as TypingPayloadInterface).action !== undefined &&
    ((payload as TypingPayloadInterface).action == TypingPayloadActionType.Started ||
      (payload as TypingPayloadInterface).action == TypingPayloadActionType.Ended)
  )
}

export function isValidMessagePayload(
  type: MessageType,
  payload: ValidMessagePayload
): payload is ValidMessagePayload {
  switch (type) {
    case MessageType.ActionTaken: {
      return isActionTakenPayload(payload)
      break
    }
    case MessageType.Auth: {
      return isAuthPayload(payload)
      break
    }
    case MessageType.Buttons: {
      return isButtonsPayload(payload)
      break
    }
    case MessageType.CreateUserAndStartConversation: {
      return isCreateUserAndStartConversationPayload(payload)
      break
    }
    case MessageType.StartConversation: {
      return isStartConversationPayload(payload)
      break
    }
    case MessageType.Text: {
      return isTextPayload(payload)
      break
    }
    case MessageType.Typing: {
      return isTypingPayload(payload)
      break
    }
    case MessageType.TakeoverEvent: {
      return isTakeoverEventPayload(payload)
      break
    }
    case MessageType.Carousel: {
      return isCarouselPayload(payload)
      break
    }
    case MessageType.Command: {
      return isCommandPayload(payload)
      break
    }
    case MessageType.Media: {
      return isMediaPayload(payload)
      break
    }
    case MessageType.MultiSelect: {
      return isMultiSelectPayload(payload)
      break
    }
    case MessageType.LocationPicker: {
      return isLocationPickerPayload(payload)
      break
    }
    case MessageType.SpacePicker: {
      return isSpacePickerPayload(payload)
      break
    }

    case MessageType.ProfileCollect: {
      return isProfileCollectPayload(payload)
      break
    }
    default: {
      console.warn('No Validation For Message Payload  Type', type)
      return false
      break
    }
  }
}
/**
 * @tsoaModel
 */
export type ValidMessagePayload =
  | AuthPayloadInterface
  | ActionTakenPayload
  | ButtonsPayloadInterface
  | CarouselPayload
  | CreateUserAndStartConversationPayloadInterface
  | CommandPayload
  | MediaPayload
  | MultiSelectPayload
  | StartConversationPayloadInterface
  | TakeoverEventPayload
  | TextPayloadInterface
  | TypingPayloadInterface
  | LocationPickerPayload
  | SpacePickerPayload
  | ProfileCollectPayload
  | VoiceNodeCommand
